import React, { useState } from "react";
import {
  Modal,
  Paper,
  Typography,
  Button,
  Box,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Description, TableChart } from "@mui/icons-material";

import { useDownloadWordDocument } from "../../../hooks/useDownloadWordDocument";
import { useGetDocumentTemplates } from "../../../hooks/useGetDocumentTemplates";

export const DownloadWordDocumentModal = ({
  token,
  open,
  close,
  documentData,
  refresh,
}) => {
  const { downloadWordDocument, loading } = useDownloadWordDocument(token);
  const { templates, loading: loadingTemplates } =
    useGetDocumentTemplates(token);

  const [selectedFormats, setSelectedFormats] = useState({
    word: true,
    excel: false,
  });

  const [selectedTemplate, setSelectedTemplate] = useState("default");
  const [abortController, setAbortController] = useState(null);

  const handleFormatChange = (format) => (event) => {
    setSelectedFormats({
      ...selectedFormats,
      [format]: event.target.checked,
    });
  };

  const handleDownloadDocument = async () => {
    if (selectedFormats.word) {
      const controller = new AbortController();
      setAbortController(controller);


      const enrichedDocumentData = {
        ...documentData,
        templateType:
          selectedTemplate !== "default" ? selectedTemplate : undefined,
      };

      const success = await downloadWordDocument(
        enrichedDocumentData,
        controller.signal,
      );
      if (success) {
        setAbortController(null);
        close();
        if (refresh) refresh();
      } else {
        setAbortController(null);
      }
    }
  };

  const handleCancel = () => {
    if (abortController) {
      abortController.abort();
      setAbortController(null);
    }
    close();
  };

  const getSelectedCount = () => {
    return Object.values(selectedFormats).filter(Boolean).length;
  };

  return (
    <Modal open={open} onClose={close}>
      <Paper
        elevation={8}
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "95%", sm: 560 },
          maxWidth: 560,
          borderRadius: "16px",
          overflow: "hidden",
          outline: "none",
        }}
      >
        <Box
          sx={{
            background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
            color: "white",
            padding: "20px",
            textAlign: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
          }}
        >
          <Description sx={{ fontSize: 48, mb: 1 }} />
          <Typography variant="h6" fontWeight="bold">
            Documento a descargar:{" "}
            {documentData.nombreDocumento || "Documento sin nombre"}
          </Typography>
        </Box>

        <Box sx={{ padding: "24px" }}>
          <Typography
            variant="body1"
            textAlign="center"
            color="text.secondary"
            sx={{ mb: 3 }}
          >
            Selecciona el formato de documento que deseas descargar:
          </Typography>

          <FormGroup sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedFormats.word}
                  onChange={handleFormatChange("word")}
                  icon={<Description />}
                  checkedIcon={<Description color="primary" />}
                />
              }
              label={<Typography>Word (.docx)</Typography>}
              sx={{ mb: 2 }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedFormats.excel}
                  onChange={handleFormatChange("excel")}
                  icon={<TableChart />}
                  checkedIcon={<TableChart color="success" />}
                  disabled
                />
              }
              label={
                <Box sx={{ opacity: 0.5 }}>
                  <Typography>Excel (.xlsx) - No disponible</Typography>
                </Box>
              }
            />
          </FormGroup>

          <Divider sx={{ mb: 2 }} />

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="template-select-label">
              Plantilla del Documento
            </InputLabel>
            <Select
              labelId="template-select-label"
              value={selectedTemplate}
              label="Plantilla del Documento"
              onChange={(e) => setSelectedTemplate(e.target.value)}
              disabled={loadingTemplates}
            >
              <MenuItem value="default">
                <em>Por defecto</em>
              </MenuItem>
              {loadingTemplates ? (
                <MenuItem disabled>
                  <CircularProgress size={20} sx={{ mr: 2 }} /> Cargando
                  plantillas...
                </MenuItem>
              ) : (
                templates?.map((template) => (
                  <MenuItem key={template} value={template}>
                    Plantilla {template}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleCancel}
              fullWidth
              sx={{
                borderRadius: "8px",
                py: 1,
                fontWeight: "bold",
              }}
            >
              {loading ? "Cancelar descarga" : "Cancelar"}
            </Button>
            <LoadingButton
              variant="contained"
              onClick={handleDownloadDocument}
              loading={loading}
              disabled={getSelectedCount() === 0}
              fullWidth
              sx={{
                borderRadius: "8px",
                py: 1,
                fontWeight: "bold",
                background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)",
                },
              }}
              startIcon={<Description />}
            >
              {loading ? "Descargando..." : `Descargar (${getSelectedCount()})`}
            </LoadingButton>
          </Box>
        </Box>
      </Paper>
    </Modal>
  );
};
